import { Injectable, signal } from '@angular/core';
import { DateTime, Duration, DurationLikeObject } from 'luxon';

@Injectable({ providedIn: 'root' })
export class CooldownService {
    private readonly currentDateTime = signal(DateTime.now());

    constructor() {
        setInterval(() => this.currentDateTime.set(DateTime.now()), 1000);
    }

    public startCooldown(cooldownKey: string, cooldownDurationOptions: DurationLikeObject): void {
        const storageKey = this.createCooldownStorageKey(cooldownKey);
        const cooldownDuration = Duration.fromObject(cooldownDurationOptions);
        const cooldownEndsAt = DateTime.now().plus(cooldownDuration).toISO();

        localStorage.setItem(storageKey, cooldownEndsAt);
        this.currentDateTime.set(DateTime.now());
    }

    public isCooldownActive(cooldownKey: string): boolean {
        return this.getRemainingCooldownSeconds(cooldownKey) > 0;
    }

    public getRemainingCooldownTimeLabel(cooldownKey: string): string {
        const remainingSeconds = this.getRemainingCooldownSeconds(cooldownKey);

        return Duration.fromObject({ seconds: remainingSeconds }).rescale().toHuman({ unitDisplay: 'long' });
    }

    private getRemainingCooldownSeconds(cooldownKey: string): number {
        const cooldownEndsAt = this.getCooldownEndsAt(cooldownKey);

        if (!cooldownEndsAt) {
            return 0;
        }

        const remainingSeconds = Math.max(0, Math.ceil(cooldownEndsAt.diff(this.currentDateTime()).as('seconds')));

        if (remainingSeconds === 0) {
            localStorage.removeItem(this.createCooldownStorageKey(cooldownKey));
        }

        return remainingSeconds;
    }

    private getCooldownEndsAt(cooldownKey: string): DateTime | null {
        const storageKey = this.createCooldownStorageKey(cooldownKey);
        const timeStamp = localStorage.getItem(storageKey);

        if (!timeStamp) {
            return null;
        }

        const cooldownEndsAt = DateTime.fromISO(timeStamp);

        if (!cooldownEndsAt.isValid) {
            localStorage.removeItem(storageKey);
            return null;
        }

        return cooldownEndsAt;
    }

    private createCooldownStorageKey(cooldownKey: string): string {
        return `COOLDOWN_${cooldownKey}`;
    }
}
